import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Search, 
  RefreshCcw,
  TrendingUp,
  Building2,
  Filter
} from "lucide-react";
import { formatCurrency } from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Funds = () => {
  const { api, isBroker } = useAuth();
  const [funds, setFunds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchFunds();
    }
  }, [searchTerm, selectedCategory]);

  const searchFunds = async () => {
    setLoading(true);
    try {
      let url = `/funds/search?query=${encodeURIComponent(searchTerm)}`;
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await api.get(url);
      setFunds(response.data);
    } catch (error) {
      toast.error("Failed to search funds");
    } finally {
      setLoading(false);
    }
  };

  const syncNavData = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/funds/sync-nav");
      toast.success(response.data.message);
      if (searchTerm.length >= 2) {
        searchFunds();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync NAV data");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="funds-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">MF Schemes</h1>
          <p className="text-gray-500">Search and explore mutual fund schemes</p>
        </div>
        {isBroker && (
          <Button
            onClick={syncNavData}
            disabled={syncing}
            variant="outline"
            data-testid="sync-nav-button"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync NAV Data"}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by scheme name (min 2 characters)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="fund-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-64" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Equity">Equity</SelectItem>
                <SelectItem value="Debt">Debt</SelectItem>
                <SelectItem value="Hybrid">Hybrid</SelectItem>
                <SelectItem value="Solution Oriented">Solution Oriented</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            {searchTerm.length < 2 
              ? "Enter at least 2 characters to search" 
              : loading 
                ? "Searching..." 
                : `Found ${funds.length} schemes`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {searchTerm.length < 2 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Search Mutual Funds</h3>
              <p className="text-gray-500">
                Enter a scheme name to find NAV and details
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : funds.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No schemes found</h3>
              <p className="text-gray-500">Try a different search term</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="kinntegra-table">
                <thead>
                  <tr>
                    <th>ISIN</th>
                    <th>Scheme Name</th>
                    <th>AMC</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Plan</th>
                    <th>NAV</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {funds.map((fund, idx) => (
                    <tr key={fund.isin || fund.scheme_code || idx} data-testid={`fund-row-${fund.scheme_code}`}>
                      <td>
                        <span className="font-mono text-sm text-gray-600">{fund.isin || "-"}</span>
                      </td>
                      <td>
                        <div className="max-w-md">
                          <p className="font-medium text-gray-800">{fund.scheme_name}</p>
                          <p className="text-sm text-gray-500">Code: {fund.scheme_code}</p>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-sm truncate max-w-[150px]">{fund.amc_code || fund.amc || "-"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
                          {fund.scheme_type || fund.category || "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">{fund.scheme_plan || "-"}</span>
                      </td>
                      <td className="font-semibold text-teal-700">
                        {fund.nav ? formatCurrency(fund.nav) : "-"}
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${
                          fund.source === "scheme_master" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-purple-100 text-purple-800"
                        }`}>
                          {fund.source === "scheme_master" ? "BSE Upload" : "AMFI"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Funds;
